import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useScoresheet } from '../../../shared/hooks/useScoresheet';
import { UIScoresheetStat, StatType } from '../../../shared/types/database';
import Toast from '../../../shared/components/ui/Toast';
import Button from '../../../shared/components/ui/Button';
import { matchVideosService } from '../../../shared/services/matchVideosService';
import PitchCanvas from '../components/PitchCanvas';
import Logs from '../components/ScoresheetLogs';
import PlayerSelection from '../components/PlayerSelection';
import { VideoPlayer } from '../../../shared/components/video';
import { VideoPlayerMethods } from '../../../shared/components/video/types';
import QuickTagInput, { QuickTag } from '../../../shared/components/ui/QuickTagInput';

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

interface MatchDetails {
  id: string;
  homeTeam: {
    id: string;
    name: string;
    logo: string;
    score: number;
  };
  awayTeam: {
    id: string;
    name: string;
    logo: string;
    score: number;
  };
}

interface ScoresheetTabProps {
  matchDetails: MatchDetails;
  // Push Events functionality moved to Config tab
  matchStatus?: {
    status: 'not_started' | 'live' | 'half_time' | 'full_time' | 'editing' | 'extra_time_1st_half' | 'extra_time_half_time' | 'extra_time_2nd_half' | 'penalty_shootout';
    current_minute: number;
  };
  enableEditingMode?: () => Promise<void>;
  disableEditingMode?: () => Promise<void>;
  videoPlayerRef?: React.MutableRefObject<VideoPlayerMethods | null>;
  onVideoContextChange?: (context: { currentVideoId: string | null; currentVideoTime?: number | null }) => void;
}

// Frontend stat definitions - all possible stats with their properties
const FRONTEND_STATS: Array<{
  key: string;
  name: string;
    category: 'distribution' | 'attack' | 'defense' | 'goalkeeper' | 'duels' | 'freekick' | 'outside' | 'errors' | 'game_flow';
  eventResult: 'success' | 'failure' | 'neutral';
}> = [
  // Distribution Stats
  { key: 'short_pass_success', name: 'Short Pass ✓', category: 'distribution', eventResult: 'success' },
  { key: 'short_pass_failure', name: 'Short Pass ✕', category: 'distribution', eventResult: 'failure' },
  { key: 'long_pass_success', name: 'Long Pass ✓', category: 'distribution', eventResult: 'success' },
  { key: 'long_pass_failure', name: 'Long Pass ✕', category: 'distribution', eventResult: 'failure' },
  { key: 'cross_success', name: 'Cross ✓', category: 'distribution', eventResult: 'success' },
  { key: 'cross_failure', name: 'Cross ✕', category: 'distribution', eventResult: 'failure' },

  // Backward Pass Stats
  { key: 'short_pass_backward_success', name: 'Short Pass Backward ✓', category: 'distribution', eventResult: 'success' },
  { key: 'short_pass_backward_failure', name: 'Short Pass Backward ✕', category: 'distribution', eventResult: 'failure' },
  { key: 'long_pass_backward_success', name: 'Long Pass Backward ✓', category: 'distribution', eventResult: 'success' },
  { key: 'long_pass_backward_failure', name: 'Long Pass Backward ✕', category: 'distribution', eventResult: 'failure' },
  { key: 'cross_backward_success', name: 'Cross Backward ✓', category: 'distribution', eventResult: 'success' },
  { key: 'cross_backward_failure', name: 'Cross Backward ✕', category: 'distribution', eventResult: 'failure' },

  // Attack Stats - Inside Box Shots
  { key: 'shot_inside_box_on_target', name: 'Inside Box On Target', category: 'attack', eventResult: 'success' },
  { key: 'shot_inside_box_off_target', name: 'Inside Box Off Target', category: 'attack', eventResult: 'failure' },
  { key: 'shot_inside_box_blocked', name: 'Inside Box Blocked', category: 'attack', eventResult: 'neutral' },
  { key: 'shot_inside_box_side_post', name: 'Inside Box Side Post', category: 'attack', eventResult: 'neutral' },
  { key: 'shot_inside_box_crossbar', name: 'Inside Box Crossbar', category: 'attack', eventResult: 'neutral' },
  { key: 'shot_inside_box_goal', name: 'Inside Box Goal', category: 'attack', eventResult: 'success' },
  
  // Attack Stats - Outside Box Shots
  { key: 'shot_outside_box_on_target', name: 'Outside Box On Target', category: 'attack', eventResult: 'success' },
  { key: 'shot_outside_box_off_target', name: 'Outside Box Off Target', category: 'attack', eventResult: 'failure' },
  { key: 'shot_outside_box_blocked', name: 'Outside Box Blocked', category: 'attack', eventResult: 'neutral' },
  { key: 'shot_outside_box_side_post', name: 'Outside Box Side Post', category: 'attack', eventResult: 'neutral' },
  { key: 'shot_outside_box_crossbar', name: 'Outside Box Crossbar', category: 'attack', eventResult: 'neutral' },
  { key: 'shot_outside_box_goal', name: 'Outside Box Goal', category: 'attack', eventResult: 'success' },
  
  // Penalties
  { key: 'penalty_scored', name: 'Penalty Scored', category: 'attack', eventResult: 'success' },
  { key: 'penalty_missed', name: 'Penalty Missed', category: 'attack', eventResult: 'failure' },
  
  // Penalty Shootout Stats
  { key: 'penalty_shootout_scored', name: 'Penalty Shootout Scored', category: 'attack', eventResult: 'success' },
  { key: 'penalty_shootout_missed', name: 'Penalty Shootout Missed', category: 'attack', eventResult: 'failure' },
  
  // Own Goal (moved from errors to attack - team that benefits from it)
  { key: 'own_goal', name: 'Own Goal', category: 'attack', eventResult: 'success' },
  { key: 'assist', name: 'Assist', category: 'attack', eventResult: 'success' },

  // Defense Stats
  { key: 'tackle_success', name: 'Tackle ✓', category: 'defense', eventResult: 'success' },
  { key: 'tackle_failure', name: 'Tackle ✕', category: 'defense', eventResult: 'failure' },
  { key: 'clearance', name: 'Clearance', category: 'defense', eventResult: 'neutral' },

  // Duels Stats
  { key: 'dribble_won', name: 'Dribbles completed', category: 'duels', eventResult: 'success' },
  { key: 'aerial_won', name: 'Arial Duels Won', category: 'duels', eventResult: 'success' },

  // Freekick Stats
  { key: 'direct_fk', name: 'Direct FK', category: 'freekick', eventResult: 'neutral' },
  { key: 'indirect_fk', name: 'Indirect FK', category: 'freekick', eventResult: 'neutral' },

  // Outside Stats
  { key: 'throw_in', name: 'Throw In', category: 'outside', eventResult: 'neutral' },
  { key: 'corner', name: 'Corner', category: 'outside', eventResult: 'neutral' },

  // Errors Stats
  { key: 'offside', name: 'Offside', category: 'errors', eventResult: 'failure' },
  { key: 'handball', name: 'Handball', category: 'errors', eventResult: 'failure' },
  { key: 'interception', name: 'Interception', category: 'defense', eventResult: 'success' },
  { key: 'foul_own_half', name: 'Foul Own Half', category: 'defense', eventResult: 'failure' },
  { key: 'foul_opp_half', name: 'Foul Opp Half', category: 'defense', eventResult: 'failure' },
  { key: 'yellow_card', name: 'Yellow', category: 'defense', eventResult: 'failure' },
  { key: 'red_card', name: 'Red', category: 'defense', eventResult: 'failure' },

  // Goalkeeper Stats - Updated with new stat keys
  { key: 'save_open_play', name: 'Save Open Play', category: 'goalkeeper', eventResult: 'success' },
  { key: 'save_penalty', name: 'Save Penalty', category: 'goalkeeper', eventResult: 'success' },
  { key: 'open_play_claim', name: 'Open Play Claim', category: 'goalkeeper', eventResult: 'success' },
  { key: 'open_play_punch', name: 'Open Play Punch', category: 'goalkeeper', eventResult: 'success' },
  { key: 'gk_interception_open_play', name: 'Interception Open Play', category: 'goalkeeper', eventResult: 'success' },
  { key: 'penalty_claim', name: 'Penalty Claim', category: 'goalkeeper', eventResult: 'neutral' },
  { key: 'penalty_punch', name: 'Penalty Punch', category: 'goalkeeper', eventResult: 'neutral' },

    // Game Flow Stats
    { key: 'substitution', name: 'Substitution', category: 'game_flow', eventResult: 'neutral' },
  ];

const ScoresheetTab: React.FC<ScoresheetTabProps> = ({ 
  matchDetails,
  matchStatus,
  enableEditingMode,
  disableEditingMode,
  videoPlayerRef: videoPlayerRefProp,
  onVideoContextChange
}) => {
  // Extract team IDs from match details
  const homeTeamId = matchDetails.homeTeam.id;
  const awayTeamId = matchDetails.awayTeam.id;
  
  // Use the scoresheet hook
  const {
    matchStats,
    loading,
    error,
    possession,
    updateStat: updateStatBase,
    saveChanges,
    setPossessionTeam
  } = useScoresheet(matchDetails.id, homeTeamId, awayTeamId);

  // Handle canvas path change with auto-pause
  const handleCanvasPathChange = (newPath: Array<{ x: number; y: number }>) => {
    setCanvasPath(newPath);
    // Auto-pause video when user starts drawing (only if video player exists)
    if (newPath.length > 0 && resolvedVideoPlayerRef.current) {
      try {
        resolvedVideoPlayerRef.current.pause();
      } catch (error) {
        // Gracefully handle if video player is not ready
        console.warn('Video player not available for pause:', error);
      }
    }
  };

  // Handle player selection with auto-pause
  const handlePlayerSelect = (playerId: string | null) => {
    setSelectedPlayerId(playerId);
    // Auto-pause video when player is selected (only if video player exists)
    if (playerId && resolvedVideoPlayerRef.current) {
      try {
        resolvedVideoPlayerRef.current.pause();
      } catch (error) {
        // Gracefully handle if video player is not ready
        console.warn('Video player not available for pause:', error);
      }
    }
  };

  // Quick tag handlers
  const handleTagCreate = (label: string) => {
    // Check if tag already exists (case-insensitive)
    const existingTag = quickTags.find(tag => tag.label.toLowerCase() === label.toLowerCase());
    if (existingTag) {
      // If exists, just select it (this will trigger auto-pause)
      if (!selectedTagIds.includes(existingTag.id)) {
        handleTagSelect(existingTag.id);
      }
      return;
    }

    // Create new tag
    const newTag: QuickTag = {
      id: `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      label,
      color: '' // Color is determined by component
    };
    setQuickTags([...quickTags, newTag]);
    // Auto-select newly created tag (this will trigger auto-pause)
    handleTagSelect(newTag.id);
  };

  const handleTagSelect = (tagId: string) => {
    if (!selectedTagIds.includes(tagId)) {
      setSelectedTagIds([...selectedTagIds, tagId]);
      
      // Auto-pause video when tag is selected (only if video player exists)
      if (resolvedVideoPlayerRef.current) {
        try {
          resolvedVideoPlayerRef.current.pause();
        } catch (error) {
          // Gracefully handle if video player is not ready
          console.warn('Video player not available for pause:', error);
        }
      }
    }
  };

  const handleTagDeselect = (tagId: string) => {
    setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
  };

  // Wrapper to reset player selection and canvas after any stat update
  const updateStat = async (
    teamId: string,
    statKey: string,
    action: 'increment' | 'decrement'
  ) => {
    // Get current video time before saving (optional - may be null)
    let videoTime: number | null = null;
    let videoId: string | null = null;
    
    // Only capture video time if video player is available
    if (resolvedVideoPlayerRef.current && currentVideoId) {
      try {
        // Try to get the most accurate current time from player, fallback to tracked state
        const playerTime = resolvedVideoPlayerRef.current.getCurrentTime();
        videoTime = playerTime !== null && playerTime !== undefined ? playerTime : (currentVideoTime > 0 ? currentVideoTime : null);
        videoId = currentVideoId;
      } catch (error) {
        // Gracefully handle if video player is not ready
        console.warn('Video player not available for time capture:', error);
        // Fallback to tracked state if available
        if (currentVideoTime > 0 && currentVideoId) {
          videoTime = currentVideoTime;
          videoId = currentVideoId;
        }
      }
    }
    
    const positionData = canvasPath.length > 0
      ? (canvasPath.length === 1
          ? { x: canvasPath[0].x, y: canvasPath[0].y }
          : canvasPath)
      : null;

    // Get selected tag labels for manual_tags
    const selectedTagLabels = selectedTagIds
      .map(id => quickTags.find(tag => tag.id === id)?.label)
      .filter((label): label is string => label !== undefined);
    
    // Pass video timing and selected tags to updateStatBase
    await updateStatBase(
      teamId, 
      statKey, 
      action, 
      selectedPlayerId || undefined, 
      positionData,
      videoTime || undefined,
      videoId || undefined,
      selectedTagLabels.length > 0 ? selectedTagLabels : undefined
    );
    
    setSelectedPlayerId(null);
    setCanvasPath([]);
    // Clear selected tags after stat is saved
    setSelectedTagIds([]);
    
    // Auto-play video from captured time (only if video time was captured)
    if (videoTime !== null && resolvedVideoPlayerRef.current) {
      try {
        resolvedVideoPlayerRef.current.seekTo(videoTime);
        // Wait a bit for seek to complete, then play
        // This prevents "play() interrupted by pause()" errors
        setTimeout(async () => {
          try {
            // play() now returns a Promise and handles errors internally
            await resolvedVideoPlayerRef.current?.play();
          } catch (error) {
            // Errors are already handled in the play() method
            // This catch is just for safety
          }
        }, 100); // Small delay to ensure seek completes
      } catch (error) {
        // Gracefully handle if video player is not ready
        console.warn('Video player not available for seek/play:', error);
      }
    }
  };

  // Create merged stats (frontend definitions + DB values)
  const mergedStats = useMemo(() => {
    const homeStats: UIScoresheetStat[] = [];
    const awayStats: UIScoresheetStat[] = [];

    FRONTEND_STATS.forEach(frontendStat => {
      // Create mock StatType from frontend definition
      const mockStatType: StatType = {
        id: frontendStat.key, // Use key as ID for frontend stats
        category: frontendStat.category,
        stat_key: frontendStat.key,
        display_name: frontendStat.name,
        short_description: '',
        event_result: frontendStat.eventResult,
        is_active: true,
        is_calculated: false,
        visible_in_reports: null,
        top_stats: null,
        sort_order: 0,
        created_at: new Date().toISOString()
      };

      // Get value directly from matchStats (includes optimistic updates)
      let homeValue = 0;
      let awayValue = 0;

      if (matchStats) {
        // Find home team stat
        const homeStat = matchStats.home_team_stats.find(s => s.stat_type.stat_key === frontendStat.key);
        homeValue = homeStat?.value || 0;

        // Find away team stat
        const awayStat = matchStats.away_team_stats.find(s => s.stat_type.stat_key === frontendStat.key);
        awayValue = awayStat?.value || 0;
      }

      // Add to respective team stats
      homeStats.push({
        stat_type: mockStatType,
        value: homeValue,
        team_id: homeTeamId
      });

      awayStats.push({
        stat_type: mockStatType,
        value: awayValue,
        team_id: awayTeamId
      });
    });

    return {
      home_team_stats: homeStats,
      away_team_stats: awayStats
    };
  }, [matchStats, homeTeamId, awayTeamId]);

  // Toast state
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [toastType, setToastType] = React.useState<'success' | 'error' | 'info'>('success');

  // Editing state
  const [showEditConfirmation, setShowEditConfirmation] = React.useState(false);
  const [showConfirmEditsDialog, setShowConfirmEditsDialog] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  // Video state
  const [videos, setVideos] = React.useState<any[]>([]);
  const [videosLoading, setVideosLoading] = React.useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = React.useState(0);
  
  // Video player ref and time tracking
  const internalVideoPlayerRef = useRef<VideoPlayerMethods | null>(null);
  const resolvedVideoPlayerRef = videoPlayerRefProp ?? internalVideoPlayerRef;
  const attachVideoPlayerRef = (instance: VideoPlayerMethods | null) => {
    resolvedVideoPlayerRef.current = instance;
  };
  const [currentVideoTime, setCurrentVideoTime] = useState<number>(0);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);

  // Player and position state
  const [selectedPlayerId, setSelectedPlayerId] = React.useState<string | null>(null);
  const [canvasPath, setCanvasPath] = React.useState<Array<{ x: number; y: number }>>([]);

  // Quick tags state (frontend-only, per match session)
  const [quickTags, setQuickTags] = React.useState<QuickTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = React.useState<string[]>([]);

  // Sync editing state with match status
  React.useEffect(() => {
    if (matchStatus?.status === 'editing') {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [matchStatus?.status]);

  // Fetch videos for this match
  const fetchVideos = React.useCallback(async () => {
    if (!matchDetails.id) return;
    
    setVideosLoading(true);
    try {
      const result = await matchVideosService.getMatchVideos(matchDetails.id);
      if (result.success && result.data) {
        setVideos(result.data);
        // Reset to first video when videos are loaded/updated
        setSelectedVideoIndex(0);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setVideosLoading(false);
    }
  }, [matchDetails.id]);

  React.useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Listen for video updates from VideoLinkModal
  React.useEffect(() => {
    const handleVideosUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ matchId: string }>;
      if (customEvent.detail.matchId === matchDetails.id) {
        fetchVideos();
      }
    };

    window.addEventListener('matchVideosUpdated', handleVideosUpdated);
    return () => {
      window.removeEventListener('matchVideosUpdated', handleVideosUpdated);
    };
  }, [matchDetails.id, fetchVideos]);

  // Track current video ID when video selection changes
  React.useEffect(() => {
    if (videos[selectedVideoIndex]) {
      setCurrentVideoId(videos[selectedVideoIndex].id);
    } else {
      setCurrentVideoId(null);
    }
  }, [videos, selectedVideoIndex]);

  // Share current video context with parent components (if requested)
  React.useEffect(() => {
    if (onVideoContextChange) {
      onVideoContextChange({
        currentVideoId,
        currentVideoTime
      });
    }
  }, [currentVideoId, currentVideoTime, onVideoContextChange]);

  // Note: getEmbedUrl removed - VideoPlayer component handles URL conversion internally

  // Save handler function
  const handleSave = async () => {
    try {
      await saveChanges();
      setToastMessage('Scoresheet changes saved successfully!');
      setToastType('success');
      setShowToast(true);
      
      // Dispatch custom event for penalty shootout score updates
      window.dispatchEvent(new CustomEvent('penaltyScoresUpdated'));
    } catch (error) {
      console.error('Error saving scoresheet:', error);
      setToastMessage('Failed to save scoresheet changes. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Editing handler functions
  const handleEnableEditing = async () => {
    try {
      if (enableEditingMode) {
        await enableEditingMode();
        setShowEditConfirmation(false);
        setToastMessage('Editing mode enabled. You can now modify match statistics.');
        setToastType('success');
        setShowToast(true);
        
        // Refresh the page after enabling editing mode
        window.location.reload();
      }
    } catch (error) {
      console.error('Error enabling editing mode:', error);
      setToastMessage('Failed to enable editing mode. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleDisableEditing = async () => {
    try {
      if (disableEditingMode) {
        await disableEditingMode();
        setShowConfirmEditsDialog(false);
        setToastMessage('Editing mode disabled. Match statistics are now locked.');
        setToastType('success');
        setShowToast(true);
        
        // Refresh the page after disabling editing mode
        window.location.reload();
      }
    } catch (error) {
      console.error('Error disabling editing mode:', error);
      setToastMessage('Failed to disable editing mode. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleShowConfirmEditsDialog = () => {
    setShowConfirmEditsDialog(true);
  };

  // Helper function to handle stat updates with match status validation
  const handleStatUpdate = async (teamId: string, statKey: string, action: 'increment' | 'decrement') => {
    // Only block if we're absolutely certain the match has ended (and not in editing mode)
    // For not_started, let the service handle validation (it will query the database)
    // This prevents false positives when status is stale or still loading
    if (matchStatus?.status === 'full_time' && !isEditing) {
      // Show toast instead of setting error state
      setToastMessage('Cannot update stats - match has ended');
      setToastType('error');
      setShowToast(true);
      return;
    }
    // Don't block for not_started - let service query database for accurate status
    
    try {
      await updateStat(teamId, statKey, action);
      
      // Reset player and canvas after insert
      setSelectedPlayerId(null);
      setCanvasPath([]);
    } catch (error) {
      console.error(`Error updating stat ${statKey}:`, error);
      setToastMessage(`Failed to update ${statKey}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  // Expose save handler to window object for parent component
  useEffect(() => {
    (window as any).scoresheetSaveHandler = handleSave;
    
    // Cleanup on unmount
    return () => {
      delete (window as any).scoresheetSaveHandler;
    };
  }, [handleSave]);


  // Helper function to render distribution stat pair (success/failure)
  const renderDistributionPair = (teamId: string, passType: string) => {
    const successStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === `${passType}_success` && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === `${passType}_success` && s.team_id === teamId
    );
    
    const failureStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === `${passType}_failure` && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === `${passType}_failure` && s.team_id === teamId
    );

    const successValue = successStat?.value || 0;
    const failureValue = failureStat?.value || 0;
    const totalAttempts = successValue + failureValue;
    const successRate = totalAttempts > 0 ? Math.round((successValue / totalAttempts) * 100) : 0;

    return (
      <div key={passType} className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800 capitalize">
            {passType.replace('_', ' ')}
          </span>
          <div className="text-xs text-gray-500">
            {successRate}%
          </div>
        </div>
        
        <div className="space-y-1">
          {/* Success Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Success</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => successStat && handleStatUpdate(teamId, successStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {successValue}
              </span>
              <button
                onClick={() => successStat && handleStatUpdate(teamId, successStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Failure Row */}
      <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Failed</span>
            </div>
        <div className="flex items-center space-x-1">
          <button
                onClick={() => failureStat && updateStat(teamId, failureStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
          >
            -
          </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {failureValue}
          </span>
          <button
                onClick={() => failureStat && handleStatUpdate(teamId, failureStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
          >
            +
          </button>
            </div>
        </div>
      </div>
    </div>
  );
  };

  // State for shot tab selection
  const [activeShotTab, setActiveShotTab] = useState<'inside_box' | 'outside_box'>('inside_box');
  
  // State for distribution tab selection
  const [activeDistributionTab, setActiveDistributionTab] = useState<'forward' | 'backward'>('forward');
  
  // State for goalkeeper tab selection
  const [activeGoalkeeperTab, setActiveGoalkeeperTab] = useState<'open_play' | 'penalty'>('open_play');

  // Helper function to render shot subcategories with tabs
  const renderShotSubcategories = (teamId: string) => {

    const shotOutcomes = [
      { key: 'on_target', label: 'On Target', color: 'green' },
      { key: 'off_target', label: 'Off Target', color: 'red' },
      { key: 'blocked', label: 'Blocked', color: 'gray' },
      { key: 'side_post', label: 'Side Post', color: 'gray' },
      { key: 'crossbar', label: 'Crossbar', color: 'gray' },
      { key: 'goal', label: 'Goal', color: 'green' }
    ];

    const currentStats = shotOutcomes.map(outcome => ({
      ...outcome,
      key: `shot_${activeShotTab}_${outcome.key}`
    }));

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-800">Shots</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveShotTab('inside_box')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeShotTab === 'inside_box' 
                  ? 'bg-white text-gray-900 shadow-sm font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Inside Box
            </button>
            <button
              onClick={() => setActiveShotTab('outside_box')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeShotTab === 'outside_box' 
                  ? 'bg-white text-gray-900 shadow-sm font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Outside Box
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-1">
          {currentStats.map(shot => {
            const stat = mergedStats.home_team_stats.find(s => 
              s.stat_type.stat_key === shot.key && s.team_id === teamId
            ) || mergedStats.away_team_stats.find(s => 
              s.stat_type.stat_key === shot.key && s.team_id === teamId
            );
            const value = stat?.value || 0;
            const colorClass = shot.color === 'green' ? 'bg-green-50 border-green-200' : 
                              shot.color === 'red' ? 'bg-red-50 border-red-200' : 
                              shot.color === 'blue' ? 'bg-blue-50 border-blue-200' :
                              shot.color === 'purple' ? 'bg-purple-50 border-purple-200' :
                              'bg-gray-50 border-gray-200';

            return (
              <div key={shot.key} className={`${colorClass} border rounded p-1`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">{shot.label}</span>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => stat && handleStatUpdate(teamId, stat.stat_type.stat_key, 'decrement')}
                      disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                    >
                      -
                    </button>
                    <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                      {value}
                    </span>
                    <button
                      onClick={() => stat && handleStatUpdate(teamId, stat.stat_type.stat_key, 'increment')}
                      disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper function to render penalty pair (scored/missed)
  const renderPenaltyPair = (teamId: string) => {
    const scoredStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_scored' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_scored' && s.team_id === teamId
    );
    
    const missedStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_missed' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_missed' && s.team_id === teamId
    );

    const scoredValue = scoredStat?.value || 0;
    const missedValue = missedStat?.value || 0;
    const totalPenalties = scoredValue + missedValue;
    const successRate = totalPenalties > 0 ? Math.round((scoredValue / totalPenalties) * 100) : 0;

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800">Penalties</span>
          <div className="text-xs text-gray-500">
            {successRate}%
          </div>
        </div>
        
        <div className="space-y-1">
          {/* Scored Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Scored</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => scoredStat && updateStat(teamId, scoredStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {scoredValue}
              </span>
              <button
                onClick={() => scoredStat && updateStat(teamId, scoredStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Missed Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Missed</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => missedStat && updateStat(teamId, missedStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {missedValue}
              </span>
              <button
                onClick={() => missedStat && handleStatUpdate(teamId, missedStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Helper function to render penalty shootout pair (scored/missed)
  const renderPenaltyShootoutPair = (teamId: string) => {
    const scoredStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_shootout_scored' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_shootout_scored' && s.team_id === teamId
    );
    
    const missedStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_shootout_missed' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_shootout_missed' && s.team_id === teamId
    );

    const scoredValue = scoredStat?.value || 0;
    const missedValue = missedStat?.value || 0;
    const totalPenalties = scoredValue + missedValue;
    const successRate = totalPenalties > 0 ? Math.round((scoredValue / totalPenalties) * 100) : 0;

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800">Penalty Shootout</span>
          <div className="text-xs text-gray-500">
            {successRate}%
          </div>
        </div>
        
        <div className="space-y-1">
          {/* Scored Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Scored</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => scoredStat && updateStat(teamId, scoredStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {scoredValue}
              </span>
              <button
                onClick={() => scoredStat && updateStat(teamId, scoredStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Missed Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Missed</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => missedStat && updateStat(teamId, missedStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {missedValue}
              </span>
              <button
                onClick={() => missedStat && updateStat(teamId, missedStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render tackles (success/failure pair)
  const renderTacklesPair = (teamId: string) => {
    const successStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'tackle_success' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'tackle_success' && s.team_id === teamId
    );
    
    const failureStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'tackle_failure' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'tackle_failure' && s.team_id === teamId
    );

    const successValue = successStat?.value || 0;
    const failureValue = failureStat?.value || 0;
    const totalAttempts = successValue + failureValue;
    const successRate = totalAttempts > 0 ? Math.round((successValue / totalAttempts) * 100) : 0;

    return (
      <div key="tackles" className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800">Tackles</span>
          <div className="text-xs text-gray-500">
            {successRate}%
          </div>
        </div>
        
        <div className="space-y-1">
          {/* Success Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Success</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => successStat && handleStatUpdate(teamId, successStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {successValue}
              </span>
              <button
                onClick={() => successStat && handleStatUpdate(teamId, successStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Failure Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Failed</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => failureStat && updateStat(teamId, failureStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {failureValue}
              </span>
              <button
                onClick={() => failureStat && handleStatUpdate(teamId, failureStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render defensive actions (clearance + interception)
  const renderDefensiveActions = (teamId: string) => {
    const clearanceStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'clearance' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'clearance' && s.team_id === teamId
    );
    
    const interceptionStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'interception' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'interception' && s.team_id === teamId
    );

    const clearanceValue = clearanceStat?.value || 0;
    const interceptionValue = interceptionStat?.value || 0;

    return (
      <div key="defensive_actions" className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800">Defensive Actions</span>
        </div>
        
        <div className="space-y-1">
          {/* Clearance Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Clearance</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => clearanceStat && updateStat(teamId, clearanceStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {clearanceValue}
              </span>
              <button
                onClick={() => clearanceStat && updateStat(teamId, clearanceStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Interception Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Interception</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => interceptionStat && updateStat(teamId, interceptionStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {interceptionValue}
              </span>
              <button
                onClick={() => interceptionStat && handleStatUpdate(teamId, interceptionStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render foul position (own half + opponent half)
  const renderFoulPosition = (teamId: string) => {
    const ownHalfStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'foul_own_half' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'foul_own_half' && s.team_id === teamId
    );
    
    const oppHalfStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'foul_opp_half' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'foul_opp_half' && s.team_id === teamId
    );

    const ownHalfValue = ownHalfStat?.value || 0;
    const oppHalfValue = oppHalfStat?.value || 0;

    return (
      <div key="foul_position" className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800">Foul Position</span>
        </div>
        
        <div className="space-y-1">
          {/* Own Half Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Own Half</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => ownHalfStat && updateStat(teamId, ownHalfStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {ownHalfValue}
              </span>
              <button
                onClick={() => ownHalfStat && handleStatUpdate(teamId, ownHalfStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Opponent Half Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Opponent Half</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => oppHalfStat && updateStat(teamId, oppHalfStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {oppHalfValue}
              </span>
              <button
                onClick={() => oppHalfStat && handleStatUpdate(teamId, oppHalfStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render cards (yellow + red)
  const renderCards = (teamId: string) => {
    const yellowStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'yellow_card' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'yellow_card' && s.team_id === teamId
    );
    
    const redStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'red_card' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'red_card' && s.team_id === teamId
    );

    const yellowValue = yellowStat?.value || 0;
    const redValue = redStat?.value || 0;

    return (
      <div key="cards" className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800">Cards</span>
        </div>
        
        <div className="space-y-1">
          {/* Yellow Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Yellow</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => yellowStat && updateStat(teamId, yellowStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {yellowValue}
              </span>
              <button
                onClick={() => yellowStat && handleStatUpdate(teamId, yellowStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Red Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs text-gray-600">Red</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => redStat && updateStat(teamId, redStat.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {redValue}
              </span>
              <button
                onClick={() => redStat && handleStatUpdate(teamId, redStat.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render goalkeeper stats with tabs
  const renderGoalkeeperStats = (teamId: string, activeTab: 'open_play' | 'penalty', setActiveTab: (tab: 'open_play' | 'penalty') => void) => {

    // Note: These stats are available but not used in the current UI
    // const openPlaySaveStat = mergedStats.home_team_stats.find(s => 
    //   s.stat_type.stat_key === 'save_open_play' && s.team_id === teamId
    // ) || mergedStats.away_team_stats.find(s => 
    //   s.stat_type.stat_key === 'save_open_play' && s.team_id === teamId
    // );

    // const penaltySaveStat = mergedStats.home_team_stats.find(s => 
    //   s.stat_type.stat_key === 'save_penalty' && s.team_id === teamId
    // ) || mergedStats.away_team_stats.find(s => 
    //   s.stat_type.stat_key === 'save_penalty' && s.team_id === teamId
    // );

    // Get penalty stats (these are the ones that should be pushed to key events)
    const penaltyClaimStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_claim' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_claim' && s.team_id === teamId
    );

    const penaltyPunchStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_punch' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'penalty_punch' && s.team_id === teamId
    );

    // Get open play stats (these are not pushed to key events)
    const openPlayClaimStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'open_play_claim' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'open_play_claim' && s.team_id === teamId
    );

    const openPlayPunchStat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'open_play_punch' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'open_play_punch' && s.team_id === teamId
    );

    const penaltyClaimValue = penaltyClaimStat?.value || 0;
    const penaltyPunchValue = penaltyPunchStat?.value || 0;
    const openPlayClaimValue = openPlayClaimStat?.value || 0;
    const openPlayPunchValue = openPlayPunchStat?.value || 0;

    return (
      <div key="goalkeeper" className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-800">Saves</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('open_play')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeTab === 'open_play'
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Open Play
            </button>
            <button
              onClick={() => setActiveTab('penalty')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeTab === 'penalty'
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Penalty
            </button>
          </div>
        </div>

        <div className="space-y-1">
          {activeTab === 'penalty' ? (
            <>
              {/* Penalty Claim Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Claim</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => penaltyClaimStat && updateStat(teamId, penaltyClaimStat.stat_type.stat_key, 'decrement')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    -
                  </button>
                  <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                    {penaltyClaimValue}
                  </span>
                  <button
                    onClick={() => penaltyClaimStat && updateStat(teamId, penaltyClaimStat.stat_type.stat_key, 'increment')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Penalty Punch Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Punch</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => penaltyPunchStat && updateStat(teamId, penaltyPunchStat.stat_type.stat_key, 'decrement')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    -
                  </button>
                  <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                    {penaltyPunchValue}
                  </span>
                  <button
                    onClick={() => penaltyPunchStat && updateStat(teamId, penaltyPunchStat.stat_type.stat_key, 'increment')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Open Play Claim Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Claim</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => openPlayClaimStat && updateStat(teamId, openPlayClaimStat.stat_type.stat_key, 'decrement')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    -
                  </button>
                  <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                    {openPlayClaimValue}
                  </span>
                  <button
                    onClick={() => openPlayClaimStat && updateStat(teamId, openPlayClaimStat.stat_type.stat_key, 'increment')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Open Play Punch Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Punch</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => openPlayPunchStat && updateStat(teamId, openPlayPunchStat.stat_type.stat_key, 'decrement')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    -
                  </button>
                  <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                    {openPlayPunchValue}
                  </span>
                  <button
                    onClick={() => openPlayPunchStat && updateStat(teamId, openPlayPunchStat.stat_type.stat_key, 'increment')}
                    disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                    className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                      matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Helper function to render single stat card (for individual stats like own goal)
  const renderSingleStat = (teamId: string, statKey: string, statName: string) => {
    const stat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === statKey && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === statKey && s.team_id === teamId
    );

    const value = stat?.value || 0;

    return (
      <div key={statKey} className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">{statName}</span>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => stat && handleStatUpdate(teamId, stat.stat_type.stat_key, 'decrement')}
              disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
              className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              -
            </button>
            <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
              {value}
            </span>
            <button
              onClick={() => stat && handleStatUpdate(teamId, stat.stat_type.stat_key, 'increment')}
              disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
              className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              +
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render substitution stat card (matches Own Goal style)
  const renderSubstitutionCard = (teamId: string) => {
    const stat = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === 'substitution' && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === 'substitution' && s.team_id === teamId
    );

    const value = stat?.value || 0;

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow mb-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">Substitutions</span>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => stat && handleStatUpdate(teamId, stat.stat_type.stat_key, 'decrement')}
              disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
              className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              -
            </button>
            <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
              {value}
            </span>
            <button
              onClick={() => stat && handleStatUpdate(teamId, stat.stat_type.stat_key, 'increment')}
              disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
              className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              +
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render stat pair (for stats that come in pairs)
  const renderStatPair = (teamId: string, stat1Key: string, stat2Key: string, categoryName: string) => {
    const stat1 = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === stat1Key && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === stat1Key && s.team_id === teamId
    );
    
    const stat2 = mergedStats.home_team_stats.find(s => 
      s.stat_type.stat_key === stat2Key && s.team_id === teamId
    ) || mergedStats.away_team_stats.find(s => 
      s.stat_type.stat_key === stat2Key && s.team_id === teamId
    );

    const value1 = stat1?.value || 0;
    const value2 = stat2?.value || 0;

    return (
      <div key={`${stat1Key}_${stat2Key}`} className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-800 capitalize">
            {categoryName}
          </span>
        </div>
        
        <div className="space-y-1">
          {/* First Stat Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              <span className="text-xs text-gray-600">{stat1?.stat_type.display_name || stat1Key.replace('_', ' ')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => stat1 && updateStat(teamId, stat1.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {value1}
              </span>
              <button
                onClick={() => stat1 && updateStat(teamId, stat1.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Second Stat Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
              <span className="text-xs text-gray-600">{stat2?.stat_type.display_name || stat2Key.replace('_', ' ')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => stat2 && updateStat(teamId, stat2.stat_type.stat_key, 'decrement')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                -
              </button>
              <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                {value2}
              </span>
              <button
                onClick={() => stat2 && handleStatUpdate(teamId, stat2.stat_type.stat_key, 'increment')}
                disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Helper function to get team name
  const getTeamName = (teamId: string) => {
    return teamId === homeTeamId ? matchDetails.homeTeam.name : matchDetails.awayTeam.name;
  };

  // Helper function to get team logo
  const getTeamLogo = (teamId: string) => {
    return teamId === homeTeamId ? matchDetails.homeTeam.logo : matchDetails.awayTeam.logo;
  };

  // Helper function to get possessing team info
  const getPossessingTeamInfo = () => {
    const possessingTeamId = possession === 'home' ? homeTeamId : awayTeamId;
    return {
      id: possessingTeamId,
      name: getTeamName(possessingTeamId),
      logo: getTeamLogo(possessingTeamId)
    };
  };

  // Helper function to get non-possessing team info
  const getNonPossessingTeamInfo = () => {
    const nonPossessingTeamId = possession === 'home' ? awayTeamId : homeTeamId;
    return {
      id: nonPossessingTeamId,
      name: getTeamName(nonPossessingTeamId),
      logo: getTeamLogo(nonPossessingTeamId)
    };
  };

  // Loading state
  if (loading) {
    return <TabLoader message="Loading scoresheet data..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Scoresheet</h3>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Always render the UI - mergedStats will have all stats with default values of 0

  const possessingTeam = getPossessingTeamInfo();
  const nonPossessingTeam = getNonPossessingTeamInfo();

  return (
    <div className="space-y-4">


      {/* Ball Possession Control */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-gray-700">BALL POSSESSION:</span>
              <div className="flex items-center space-x-1">
                <span className="text-lg">⚽</span>
                <span className="text-xs text-gray-500">with</span>
              </div>
            </div>
            
            {/* Team Cards */}
            <div className="flex items-center space-x-3">
              {/* Home Team Card */}
              <div 
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all cursor-pointer ${
                  possession === 'home' 
                    ? 'border-green-500 bg-green-50 shadow-md' 
                    : 'border-gray-200 bg-gray-50'
                }`}
                onClick={() => setPossessionTeam('home')}
              >
                {matchDetails.homeTeam.logo && matchDetails.homeTeam.logo.startsWith('http') ? (
                  <img 
                    src={matchDetails.homeTeam.logo} 
                    alt={`${matchDetails.homeTeam.name} logo`}
                    className="w-4 h-4 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
                <span className={`text-sm font-medium ${possession === 'home' ? 'text-green-700' : 'text-gray-600'}`}>
                  {matchDetails.homeTeam.name}
                </span>
              </div>

              {/* VS Separator */}
              <span className="text-gray-400 font-medium">VS</span>

              {/* Away Team Card */}
              <div 
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all cursor-pointer ${
                  possession === 'away' 
                    ? 'border-green-500 bg-green-50 shadow-md' 
                    : 'border-gray-200 bg-gray-50'
                }`}
                onClick={() => setPossessionTeam('away')}
              >
                {matchDetails.awayTeam.logo && matchDetails.awayTeam.logo.startsWith('http') ? (
                  <img 
                    src={matchDetails.awayTeam.logo} 
                    alt={`${matchDetails.awayTeam.name} logo`}
                    className="w-4 h-4 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
                <span className={`text-sm font-medium ${possession === 'away' ? 'text-green-700' : 'text-gray-600'}`}>
                  {matchDetails.awayTeam.name}
                </span>
              </div>
            </div>
          </div>

          {/* Push Key Events Button and Edit Button - Right Aligned */}
          <div className="flex items-center space-x-2">
            {/* Edit Button - Only show when match is full time and not in editing mode */}
            {matchStatus?.status === 'full_time' && !isEditing && (
              <Button
                onClick={() => setShowEditConfirmation(true)}
                variant="outline"
                size="sm"
                className="min-w-[80px] transition-all duration-200 border-2 border-gray-400 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                }
              >
                Edit
              </Button>
            )}

            {/* Confirm Edits Button - Only show when in editing mode */}
            {isEditing && (
              <Button
                onClick={handleShowConfirmEditsDialog}
                variant="primary"
                size="sm"
                className="min-w-[120px] transition-all duration-200 bg-green-600 hover:bg-green-700 text-white"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                }
              >
                Confirm Edits
              </Button>
            )}

            {/* Push Events functionality moved to Config tab */}
          </div>

        </div>
      </div>

      {/* Event Tracking Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr_1fr] gap-4">
        {/* Possessing Team - Attacking */}
        <div className="bg-white rounded-lg border-2 border-green-200 p-3">
          <div className="flex items-center space-x-2 mb-3">
            {possessingTeam.logo && possessingTeam.logo.startsWith('http') ? (
              <img 
                src={possessingTeam.logo} 
                alt={`${possessingTeam.name} logo`}
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className="text-lg hidden">{possessingTeam.logo}</span>
            <h3 className="text-sm font-semibold text-green-700">{possessingTeam.name} - Attacking</h3>
          </div>

          {/* Substitution Card */}
          {renderSubstitutionCard(possessingTeam.id)}

          {/* Distribution */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Distribution</h4>
            
            {/* Short Pass with Forward/Backward tabs - Full Width */}
            <div className="mb-3">
              <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-800">Short Pass</span>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setActiveDistributionTab('forward')}
                      className={`px-2 py-1 text-xs rounded-md transition-colors ${
                        activeDistributionTab === 'forward' 
                          ? 'bg-white text-gray-900 shadow-sm font-medium' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Forward
                    </button>
                    <button
                      onClick={() => setActiveDistributionTab('backward')}
                      className={`px-2 py-1 text-xs rounded-md transition-colors ${
                        activeDistributionTab === 'backward' 
                          ? 'bg-white text-gray-900 shadow-sm font-medium' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Backward
                    </button>
            </div>
          </div>

                <div className="space-y-1">
                  {/* Success Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-gray-600">Success</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => {
                          const statKey = activeDistributionTab === 'forward' ? 'short_pass_success' : 'short_pass_backward_success';
                          const stat = mergedStats.home_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          ) || mergedStats.away_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          );
                          stat && updateStat(possessingTeam.id, stat.stat_type.stat_key, 'decrement');
                        }}
                        disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                      >
                        -
                      </button>
                      <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                        {(() => {
                          const statKey = activeDistributionTab === 'forward' ? 'short_pass_success' : 'short_pass_backward_success';
                          const stat = mergedStats.home_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          ) || mergedStats.away_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          );
                          return stat?.value || 0;
                        })()}
                      </span>
                      <button
                        onClick={() => {
                          const statKey = activeDistributionTab === 'forward' ? 'short_pass_success' : 'short_pass_backward_success';
                          const stat = mergedStats.home_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          ) || mergedStats.away_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          );
                          stat && handleStatUpdate(possessingTeam.id, stat.stat_type.stat_key, 'increment');
                        }}
                        disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                      >
                        +
                      </button>
            </div>
          </div>

                  {/* Failure Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                      <span className="text-xs text-gray-600">Failed</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => {
                          const statKey = activeDistributionTab === 'forward' ? 'short_pass_failure' : 'short_pass_backward_failure';
                          const stat = mergedStats.home_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          ) || mergedStats.away_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          );
                          stat && updateStat(possessingTeam.id, stat.stat_type.stat_key, 'decrement');
                        }}
                        disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                      >
                        -
                      </button>
                      <span className="text-xs font-bold text-gray-900 min-w-[1rem] text-center">
                        {(() => {
                          const statKey = activeDistributionTab === 'forward' ? 'short_pass_failure' : 'short_pass_backward_failure';
                          const stat = mergedStats.home_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          ) || mergedStats.away_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          );
                          return stat?.value || 0;
                        })()}
                      </span>
                      <button
                        onClick={() => {
                          const statKey = activeDistributionTab === 'forward' ? 'short_pass_failure' : 'short_pass_backward_failure';
                          const stat = mergedStats.home_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          ) || mergedStats.away_team_stats.find(s => 
                            s.stat_type.stat_key === statKey && s.team_id === possessingTeam.id
                          );
                          stat && handleStatUpdate(possessingTeam.id, stat.stat_type.stat_key, 'increment');
                        }}
                        disabled={matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)}
                className={`w-3.5 h-3.5 rounded-full transition-colors flex items-center justify-center text-xs font-bold ${
                  matchStatus?.status === 'not_started' || (matchStatus?.status === 'full_time' && !isEditing)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>

            {/* Long Pass now standalone */}
            <div className="grid grid-cols-1 gap-3">
              {renderDistributionPair(possessingTeam.id, 'long_pass')}
            </div>
            
            {/* Dribbles completed - below Long Pass */}
            <div className="mt-3">
              {renderSingleStat(possessingTeam.id, 'dribble_won', 'Dribbles completed')}
            </div>
            
            {/* Arial Duels Won - below Dribbles completed */}
            <div className="mt-3">
              {renderSingleStat(possessingTeam.id, 'aerial_won', 'Arial Duels Won')}
            </div>
          </div>

          {/* Attack */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Attack</h4>
            
            {/* Assist - first in Attack */}
            <div className="mb-3 max-w-md">
              {renderSingleStat(possessingTeam.id, 'assist', 'Assist')}
            </div>
            
            {/* Cross - above Shots */}
            <div className="mb-3">
              {renderDistributionPair(possessingTeam.id, 'cross')}
            </div>
            
            {/* Shots container made narrower by stacking items inside shots */}
            <div className="grid grid-cols-1 gap-3">
              {renderShotSubcategories(possessingTeam.id)}
            </div>

            {/* Penalties - more compact width */}
            <div className="mt-4 mb-4 max-w-md">
              {renderPenaltyPair(possessingTeam.id)}
            </div>

            {/* Own Goal - more compact width */}
            <div className="mt-4 mb-4 max-w-md">
              {renderSingleStat(possessingTeam.id, 'own_goal', 'Own Goal')}
            </div>
          </div>

          {/* Set Pieces */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Set Pieces</h4>
            
            {/* Corner - above Freekick */}
            <div className="mb-3">
              {renderSingleStat(possessingTeam.id, 'corner', 'Corner')}
            </div>
            
            {/* Freekick */}
            <div className="mb-3">
              {renderStatPair(possessingTeam.id, 'direct_fk', 'indirect_fk', 'Freekick')}
            </div>
            
            {/* Throw In */}
            <div className="grid grid-cols-1 gap-3">
              {renderSingleStat(possessingTeam.id, 'throw_in', 'Throw In')}
            </div>
          </div>

          {/* Errors */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Errors</h4>
            
            <div className="grid grid-cols-1 gap-3">
              {renderSingleStat(possessingTeam.id, 'offside', 'Offside')}
              {renderSingleStat(possessingTeam.id, 'handball', 'Handball')}
            </div>
          </div>
        </div>

        {/* Middle column - Video embed + Pitch (sticky together) */}
        <div className="hidden lg:flex flex-col">
          <div className="sticky top-4 flex flex-col gap-4">
          {videosLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-sm">Loading videos...</span>
              </div>
            </div>
          ) : videos.length > 0 ? (
            <div className="space-y-4">
              {/* Quick Tags Input Box */}
              <QuickTagInput
                tags={quickTags}
                selectedTagIds={selectedTagIds}
                onTagCreate={handleTagCreate}
                onTagSelect={handleTagSelect}
                onTagDeselect={handleTagDeselect}
              />

              {/* Match Videos */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800">Match Videos</h3>
                {videos.length > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedVideoIndex(prev => (prev - 1 + videos.length) % videos.length)}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                      title="Previous video"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-xs text-gray-600 min-w-[3rem] text-center">
                      {selectedVideoIndex + 1} / {videos.length}
                    </span>
                    <button
                      onClick={() => setSelectedVideoIndex(prev => (prev + 1) % videos.length)}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                      title="Next video"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <VideoPlayer
                  ref={attachVideoPlayerRef}
                  key={`video-${videos[selectedVideoIndex].id}-${videos[selectedVideoIndex].video_url}`}
                  videoUrl={videos[selectedVideoIndex].video_url}
                  className="w-full h-full"
                  controls={true}
                  onTimeUpdate={(time) => setCurrentVideoTime(time)}
                />
              </div>
              {videos.length > 1 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Use arrows to switch between {videos.length} videos
                </p>
              )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-col items-center justify-center h-64 text-gray-500">
              <div className="mb-3">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-600 mb-1">No Videos Linked</h3>
              <p className="text-xs text-center text-gray-500">Add video links for this match to score</p>
            </div>
          )}
          
          {/* Football Pitch Canvas */}
          <div className="mt-4">
            <PitchCanvas 
              selectedPath={canvasPath}
              onPathChange={handleCanvasPathChange}
            />
          </div>
          
          {/* Player Selection */}
          <div className="mt-4">
            <PlayerSelection 
              matchId={matchDetails?.id}
              leftTeamId={possessingTeam?.id}
              rightTeamId={nonPossessingTeam?.id}
              leftTeamName={possessingTeam?.name}
              rightTeamName={nonPossessingTeam?.name}
              selectedPlayerId={selectedPlayerId}
              onPlayerSelect={handlePlayerSelect}
            />
          </div>
          </div>
        </div>

        {/* Non-Possessing Team - Defending */}
        <div className="bg-white rounded-lg border-2 border-red-200 p-3">
          <div className="flex items-center space-x-2 mb-3">
            {nonPossessingTeam.logo && nonPossessingTeam.logo.startsWith('http') ? (
              <img 
                src={nonPossessingTeam.logo} 
                alt={`${nonPossessingTeam.name} logo`}
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className="text-lg hidden">{nonPossessingTeam.logo}</span>
            <h3 className="text-sm font-semibold text-red-700">{nonPossessingTeam.name} - Defending</h3>
          </div>

          {/* Substitution Card */}
          {renderSubstitutionCard(nonPossessingTeam.id)}

          {/* Defence */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Defence</h4>
            {/* Stack vertically to reduce column width */}
            <div className="grid grid-cols-1 gap-3">
              {renderTacklesPair(nonPossessingTeam.id)}
              {renderDefensiveActions(nonPossessingTeam.id)}
            </div>
            
            {/* Arial Duels Won - below Defensive Actions */}
            <div className="mt-3">
              {renderSingleStat(nonPossessingTeam.id, 'aerial_won', 'Arial Duels Won')}
            </div>
            
            {/* Continue with remaining defence stats */}
            <div className="grid grid-cols-1 gap-3 mt-3">
              {renderFoulPosition(nonPossessingTeam.id)}
              {renderCards(nonPossessingTeam.id)}
            </div>
          </div>

          {/* Goalkeeper */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Goalkeeper</h4>
            
            {/* Interception - individual stat above Saves */}
            <div className="mb-3">
              {renderSingleStat(nonPossessingTeam.id, 'gk_interception_open_play', 'Interception Open Play')}
            </div>
            
            {/* Saves section */}
            {renderGoalkeeperStats(nonPossessingTeam.id, activeGoalkeeperTab, setActiveGoalkeeperTab)}
          </div>

          {/* Set Pieces */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Set Pieces</h4>
            
            {/* Corner - above Freekick */}
            <div className="mb-3">
              {renderSingleStat(nonPossessingTeam.id, 'corner', 'Corner')}
            </div>
            
            {/* Freekick */}
            <div className="mb-3">
              {renderStatPair(nonPossessingTeam.id, 'direct_fk', 'indirect_fk', 'Freekick')}
            </div>
            
            {/* Throw In */}
            <div className="grid grid-cols-1 gap-3">
              {renderSingleStat(nonPossessingTeam.id, 'throw_in', 'Throw In')}
            </div>
          </div>

          {/* Errors */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Errors</h4>
            
            <div className="grid grid-cols-1 gap-3">
              {renderSingleStat(nonPossessingTeam.id, 'offside', 'Offside')}
              {renderSingleStat(nonPossessingTeam.id, 'handball', 'Handball')}
            </div>
          </div>

        </div>
      </div>

      {/* Penalty Shootout Section - Always visible at bottom */}
      <div className="mt-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-purple-800 flex items-center space-x-2">
            <span className="text-2xl">🥅</span>
            <span>Penalty Shootout</span>
          </h3>
          <div className="text-sm text-purple-600 font-medium">
            Track penalty shootout goals
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Home Team Penalty Shootout */}
          <div className="bg-white rounded-lg border border-purple-200 p-3">
            <div className="flex items-center space-x-2 mb-3">
              {matchDetails.homeTeam.logo && matchDetails.homeTeam.logo.startsWith('http') ? (
                <img 
                  src={matchDetails.homeTeam.logo} 
                  alt={`${matchDetails.homeTeam.name} logo`}
                  className="w-6 h-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              <h4 className="text-sm font-semibold text-purple-700">{matchDetails.homeTeam.name}</h4>
            </div>
            {renderPenaltyShootoutPair(matchDetails.homeTeam.id)}
          </div>
          
          {/* Away Team Penalty Shootout */}
          <div className="bg-white rounded-lg border border-purple-200 p-3">
            <div className="flex items-center space-x-2 mb-3">
              {matchDetails.awayTeam.logo && matchDetails.awayTeam.logo.startsWith('http') ? (
                <img 
                  src={matchDetails.awayTeam.logo} 
                  alt={`${matchDetails.awayTeam.name} logo`}
                  className="w-6 h-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              <h4 className="text-sm font-semibold text-purple-700">{matchDetails.awayTeam.name}</h4>
            </div>
            {renderPenaltyShootoutPair(matchDetails.awayTeam.id)}
          </div>
        </div>
      </div>

      {/* Realtime Scoresheet Logs */}
      <div className="mt-6">
        {/* Dark, terminal-like log viewer */}
        <Logs 
          matchId={matchDetails.id} 
          teamIdToName={{ [matchDetails.homeTeam.id]: matchDetails.homeTeam.name, [matchDetails.awayTeam.id]: matchDetails.awayTeam.name }}
        />
      </div>

      {/* Help Button */}
      <div className="fixed bottom-6 right-6">
        <button className="w-12 h-12 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition-colors flex items-center justify-center">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
          </svg>
        </button>
      </div>

      {/* Edit Confirmation Dialog */}
      {showEditConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Enable Editing Mode</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to enable editing mode for this match? This will allow you to modify all match statistics even though the match has ended.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowEditConfirmation(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleEnableEditing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Enable Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Edits Dialog */}
      {showConfirmEditsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Confirm All Changes</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to confirm all changes and exit editing mode? This will save all modifications and lock the match statistics.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmEditsDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Continue Editing
              </button>
              <button
                onClick={handleDisableEditing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Confirm Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default ScoresheetTab;